import PaginatedTable from '../../components/ui/PaginatedTable';
import PermissionButton from "../../components/ui/PermissionButton";
import { useEffect, useMemo, useState } from "react";
import {
  X,
  BookOpen,
  Landmark,
  ArrowDownCircle,
  ArrowUpCircle,
  FileText,
} from "lucide-react";

export default function AccountDetailsModal({ account, onClose }) {
  const [transactions, setTransactions] = useState([]);
  const [opening, setOpening] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!account) return;

    loadLedger();
  }, [account]);

  async function loadLedger() {
    setLoading(true);

    try {
      const res = await fetch(
        `/api/trial-balance/${account.id}`,
        {
          credentials: "include",
        },
      );

      const data = await res.json();

      if (data.status === "success") {
        setTransactions(data.transactions || []);
        setOpening(Number(data.opening || 0));
      }
    } finally {
      setLoading(false);
    }
  }

  if (!account) return null;

  const closing =
    opening + Number(account.debit || 0) - Number(account.credit || 0);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center">
      <div className="w-full max-w-5xl rounded-2xl bg-[#0b0b0f] border border-white/10 overflow-hidden">
        {/* Header */}

        <div className="flex justify-between items-center px-8 py-6 border-b border-white/10">
          <div>
            <h2 className="text-2xl font-serif">Ledger Account Details</h2>

            <p className="text-sm text-gray-400 mt-1">
              {account.code} • {account.account}
            </p>
          </div>

          <PermissionButton permissionModule="accounts"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/10"
          >
            <X size={18} />
          </PermissionButton>
        </div>

        <div className="p-8 space-y-8">
          {/* Summary */}

          <div className="grid md:grid-cols-5 gap-5">
            <Card icon={BookOpen} title="Account Code" value={account.code} />

            <Card icon={Landmark} title="Group" value={account.group} />

            <Card
              icon={ArrowDownCircle}
              title="Opening"
              value={`₹${opening.toLocaleString("en-IN")}`}
            />

            <Card
              icon={ArrowDownCircle}
              title="Debit"
              value={`₹${Number(account.debit).toLocaleString("en-IN")}`}
              green
            />

            <Card
              icon={ArrowUpCircle}
              title="Credit"
              value={`₹${Number(account.credit).toLocaleString("en-IN")}`}
              red
            />
          </div>

          {/* Balance */}

          <div className="rounded-2xl bg-gradient-to-r from-amber-400/10 to-yellow-500/5 border border-amber-400/20 p-6">
            <p className="text-xs uppercase tracking-widest text-gray-400">
              Closing Balance
            </p>

            <h2 className="text-4xl font-serif text-amber-400 mt-3">
              ₹{Math.abs(closing).toLocaleString()}
            </h2>

            <p className="mt-2 text-sm text-gray-400">
              {closing >= 0 ? "Debit Balance" : "Credit Balance"}
            </p>
          </div>

          {/* Transactions */}

          <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
            <div className="px-6 py-5 border-b border-white/10">
              <h3 className="font-serif text-lg">Recent Transactions</h3>
            </div>

            {loading ? (
              <div className="text-center py-10">Loading ledger...</div>
            ) : (
              <PaginatedTable className="w-full text-sm">
                <thead className="bg-black/30 text-gray-400">
                  <tr>
                    <th className="text-left p-4">Date</th>
                    <th className="text-left p-4">Voucher</th>
                    <th className="text-left p-4">Narration</th>
                    <th className="text-right p-4">Debit</th>
                    <th className="text-right p-4">Credit</th>
                    <th className="text-right p-4">Balance</th>
                  </tr>
                </thead>

                <tbody>
                  {transactions.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="text-center py-8 text-gray-500"
                      >
                        No Transactions
                      </td>
                    </tr>
                  ) : (
                    transactions.map((t) => (
                      <tr
                        key={t.id}
                        className="border-t border-white/10 hover:bg-white/5"
                      >
                        <td className="p-4">{t.date}</td>

                        <td className="p-4 font-medium">{t.voucher}</td>

                        <td className="p-4">{t.narration}</td>

                        <td className="p-4 text-right text-emerald-400">
                          {t.debit ? `₹${t.debit.toLocaleString()}` : "-"}
                        </td>

                        <td className="p-4 text-right text-red-400">
                          {t.credit ? `₹${t.credit.toLocaleString()}` : "-"}
                        </td>

                        <td className="p-4 text-right font-semibold text-amber-400">
                          ₹{Number(t.balance).toLocaleString("en-IN")}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </PaginatedTable>
            )}
          </div>
        </div>

        {/* Footer */}

        <div className="flex justify-end gap-3 px-8 py-5 border-t border-white/10">
          <PermissionButton permissionModule="accounts" className="px-5 py-2 rounded-xl border border-white/10 hover:bg-white/5 flex items-center gap-2">
            <FileText size={16} />
            Print Ledger
          </PermissionButton>

          <PermissionButton permissionModule="accounts"
            onClick={onClose}
            className="px-6 py-2 rounded-xl bg-amber-400 text-black font-semibold"
          >
            Close
          </PermissionButton>
        </div>
      </div>
    </div>
  );
}

function Card({ icon: Icon, title, value, green, red }) {
  return (
    <div className="rounded-xl bg-white/5 border border-white/10 p-5">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-amber-400/10 flex items-center justify-center">
          <Icon size={18} className="text-amber-400" />
        </div>

        <span className="text-xs uppercase tracking-widest text-gray-400">
          {title}
        </span>
      </div>

      <h3
        className={`text-xl font-semibold ${
          green ? "text-emerald-400" : red ? "text-red-400" : "text-white"
        }`}
      >
        {value}
      </h3>
    </div>
  );
}
